import {antibodyDomainColumnCustomEval} from 'antibodies/registration-table/antibodyDomainColumnCustomEval';
import {getObjectTypeForModelTypeConstant} from 'app-core/framework/CoreObjectIntrospectionUtils';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {smilesColumnCustomEval} from 'notebook/integrations/chemistry/custom-evals/SmilesValidationCustomEval';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
  RegistrationTableInsertType,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import {getFrontEndMetadataForInterfaces} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {
  defaultStructuredTableColumnsForRegisterBioentity,
  filterOutNonDefaultColumns,
  getBioentityPrimarySamplesFromStructuredTable,
  getTagSchemaForStructuredTable,
  inferTagSchemaItemTypeFromMetadata,
} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {makeArchiveBioentitiesActionFunction} from 'notebook/structured-tables/action-functions/archiveBioentitiesActionFunction';
import {createDatasetWithPlotsActionFunction} from 'notebook/structured-tables/action-functions/createDatasetWithPlotsActionFunction';
import {createRequestActionFunction} from 'notebook/structured-tables/action-functions/createRequestActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {registerActionFunction} from 'notebook/structured-tables/action-functions/registerActionFunction';
import registerRowsActionFunction from 'notebook/structured-tables/action-functions/registerRowsActionFunction';
import {updateRegistrationTableSettingsActionFunction} from 'notebook/structured-tables/action-functions/updateRegistrationTableSettingsActionFunction';
import {validateStructuredTableActionFunction} from 'notebook/structured-tables/action-functions/validateStructuredTableActionFunction';
import {basesColumnCustomEval} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {RegistrationTableRowUpdater} from 'notebook/structured-tables/row-updaters/RegistrationTableRowUpdater';
import {
  basesHELMColumnCustomEval,
  basesIDTColumnCustomEval,
} from 'notebook/structured-tables/sequence-notations/ValidateNotationsResolver';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {
  ActionFunctions,
  BaseStructuredTable,
  CellErrorPosition,
  EvalTableView,
  GetCustomEvalArgsOptions,
  OverflowMenuActionFunction,
  PrimarySample,
  SampleLookupInfo,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableDomainGraphType,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
  TableValidationFn,
  ToolbarButtonActionFunction,
} from 'notebook/structured-tables/StructuredTableConstants';
import {shouldValidateStructuredTableSchema} from 'notebook/structured-tables/util/shouldValidateStructuredTable';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CellIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BaseTypedValueType,
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
  RegistrationTableMetadata,
  TableColumnType,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {getBioentitySummaryFromTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {getStore} from 'redux-stores/Store';
import {isEntityCreationFeatureEnabled} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfig';
import {EntityCreationFeatureType} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfigTypes';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {isTagSchema} from 'schemas/SchemaPredicates';
import {Optional} from 'util/Types';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

export type GenericRegistrationStructuredTable<TIds> = BaseStructuredTable<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
  TIds
>;

export type RegistrationStructuredTable = GenericRegistrationStructuredTable<ClientId>;
export type FromServerRegistrationStructuredTable = GenericRegistrationStructuredTable<FromServerId>;
export type ToServerRegistrationStructuredTable = GenericRegistrationStructuredTable<ToServerId>;

export type RegistrationStructuredTableRow = StructuredTableRow<RegistrationStructuredTableRowMetadata>;

// An error message to show if the bioentity is listed twice in a REGISTER_EXISTING_ENTITIES table
export const ALREADY_ENTERED_ERROR = 'This entity already exists in the table';

function getErrorsForDuplicateExistingBioentitiesInNonFinalizedRows(
  table: RegistrationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>
): Array<CellErrorPosition> {
  // This check is only valid if we're registering existing entities
  if (table.metadata.insertType !== RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES) {
    return [];
  }
  let existingBioentityPositionsById: {
    [bioentityId: string]: Array<{columnIndex: number; rowIndex: number; isFinalized: boolean}>;
  } = {};
  table.rows.forEach((row, rowIndex) => {
    const {isFinalized} = row;
    row.cells.forEach((cell, columnIndex) => {
      const evaluatedCell = computedTable[rowIndex][columnIndex];
      const bioentitySummary = getBioentitySummaryFromTypedValue(evaluatedCell.value);
      if (
        table.columns[columnIndex].columnType === TableColumnType.EXISTING_BIOENTITY &&
        bioentitySummary != null
      ) {
        existingBioentityPositionsById = {
          ...existingBioentityPositionsById,
          [bioentitySummary.id]: [
            ...(existingBioentityPositionsById[bioentitySummary.id] != null
              ? existingBioentityPositionsById[bioentitySummary.id]
              : []),
            {columnIndex, rowIndex, isFinalized},
          ],
        };
      }
    });
  });
  const errors: Array<CellErrorPosition> = [];
  for (const bioentityId of Object.keys(existingBioentityPositionsById)) {
    const positionsFound = existingBioentityPositionsById[bioentityId];
    // If all of the positions are finalized, then we ended up with multiple rows with the same bioentity
    // because of merging.  This is okay and not considered an error.
    const allFinalized = positionsFound.every(({isFinalized}) => isFinalized);
    if (positionsFound.length > 1 && !allFinalized) {
      positionsFound.forEach(({columnIndex, rowIndex}) => {
        errors.push({
          columnIndex,
          rowIndex,
          error: {
            type: 'Error',
            message: ALREADY_ENTERED_ERROR,
          },
        });
      });
    }
  }
  return errors;
}

// An error message to show if we're trying to register an already registered item
export const ALREADY_REGISTERED_ERROR = 'This entity has already been registered';

/**
 * Returns a CustomEvalResult version of the cell which may have an error added to the typed value if
 * the following conditions are true:
 *
 * - the column is an EXISTING_BIOENTITY column
 * - the table is in REGISTER_EXISTING_ENTITIES mode
 * - the typedValue given is currently registered, (i.e. in a folder whose source type is a Registry)
 *
 * If those conditions are true, we add an error attribute onto the cell saying the value has already
 * been registered.
 *
 * This row also always returns a locked value of false for the cell as we want to allow the user to
 * modify the cell and correct the error.  In the case that the row is finalized, i.e. has already
 * registered the entity contained in the typedValue, this function should not be called as the cell
 * should be locked and the user should not be allowed to edit the value.  Which in this case, the
 * customEval does not know if that is the case.  Instead, we rely on the `getCustomEvalFn` to correctly
 * not assign this function as the customEval function.
 *
 * @param typedValue - the typedValue to check if it's registered
 * @param columnDefinition - the current column the typedValue is in
 * @param cellDependencies - this argument is ignored as this customEval function does not have any deps
 * @param metadata - the Registration table's metadata
 */
function checkRegisteredStatus(
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: RegistrationStructuredTableMetadata;
    rowMetadata: RegistrationStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (
    columnDefinition != null &&
    columnDefinition.type === TableColumnType.EXISTING_BIOENTITY &&
    tableMetadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES &&
    typedValue?.type === BaseTypedValueType.BIOENTITY
  ) {
    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        type: ResolutionQueryType.CUSTOM,
        customResolutionQueryType:
          CustomResolutionQueryType.REGISTRATION_TABLE_CHECK_EXISTING_BIOENTITY_REGISTERED_STATUS,
        bioentityId: typedValue.bioentitySummary.id,
        columnDefinition,
        typedValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  }
  return {val: typedValue, error: null, locked: false};
}

/**
 * This custom eval function returns the tag value that's pertinent for the
 * current column from the cell dependencies array. This should only happen when
 * the following conditions are met:
 *
 * - we are in the REGISTER_EXISTING_ENTITIES mode in the Registration table
 * - we have a bioentity in the first cellDependencies array
 * - we are in a column which is not an EXISTING_BIOENTITY column
 * - the typedValue for the current cell is empty
 *
 * When those conditions are met, then this function will return the appropriate
 * tag value from the existing bioentity given the current column.  In all other
 * cases, it will return the current typedValue.
 *
 * @param typedValue - the typedValue of the current cell
 * @param columnDefinition - the current column the typedValue is in
 * @param cellDependencies - an array containing the existing bioentity for the row
 * @param metadata - the Registration table's metadata
 */
function lookupTagValue(
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: RegistrationStructuredTableMetadata;
    rowMetadata: RegistrationStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (
    columnDefinition != null &&
    columnDefinition.type !== TableColumnType.EXISTING_BIOENTITY &&
    tableMetadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES &&
    cellDependencies.length > 0 &&
    isTypedValueEmpty(typedValue)
  ) {
    const bioentityTypedValue = cellDependencies[0]!;
    if (!bioentityTypedValue || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
      return {val: typedValue, error: null, locked: false};
    }
    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        type: ResolutionQueryType.CUSTOM,
        customResolutionQueryType:
          CustomResolutionQueryType.REGISTRATION_TABLE_SCHEMA_TAGS_FROM_EXISTING_BIOENTITY,
        bioentityId: bioentityTypedValue.bioentitySummary.id,
        columnDefinition,
        typedValue,
      },
      makeTypedValueFromCustomResolvedValue
    );
    const locked = columnDefinition.type === TableColumnType.SMILES;
    return {val: resolvedValue, error, locked};
  }
  return {val: typedValue, error: null, locked: false};
}

export const registrationTableArchiveBioentitiesActionFunction: ToolbarButtonActionFunction<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata
> &
  OverflowMenuActionFunction<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata> =
  makeArchiveBioentitiesActionFunction({
    analyticsActionForPerfTracking: 'archive_registration_structured_table_rows',
    analyticsMetadata: {},
  });

export class RegistrationStructuredTableAdapter extends GenericStructuredTableAdapter<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata
> {
  /**
   * The Registration table's display name is dependent on the insertType of the
   * table. This function returns a display name that's appropriate for the given
   * table's insertType.
   */
  getDisplayName(structuredTable: RegistrationStructuredTable): string {
    return structuredTable.metadata.insertType === RegistrationTableInsertType.CREATE_NEW_ENTITIES
      ? 'Register new entities'
      : 'Register existing entities';
  }

  getBlankRowMetadata(
    tableMetadata: RegistrationStructuredTableMetadata
  ): RegistrationStructuredTableRowMetadata {
    return {};
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: RegistrationStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return filterOutNonDefaultColumns(
      super.getInitialColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: RegistrationStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const [tagSchema] = schemas
      .filter((schema) => isTagSchema(schema))
      .map((schema) => schema as unknown as TagSchema);

    const bioentityExists = metadata.insertType !== RegistrationTableInsertType.CREATE_NEW_ENTITIES;
    const implementedSchemaInterfaces = metadata.implementedSchemaInterfaces ?? [];

    let entityNameColumnConfiguration: 'REQUIRED' | 'READONLY' | 'ABSENT' = 'ABSENT';
    if (tagSchema.useOrganizationCollectionAliasForDisplayLabel) {
      // If the chip is only going to contain the Registry ID, then show a read-only column for the name
      // which allows the user to see the name of the entity as well as the Registry ID
      entityNameColumnConfiguration = 'READONLY';
    }
    if (RegistryConstants.LABELING_STRATEGIES_REQUIRING_NAME.includes(metadata.labelingStrategy)) {
      entityNameColumnConfiguration = 'REQUIRED';
    }

    return defaultStructuredTableColumnsForRegisterBioentity({
      baseColumns: super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      }),
      bioentityExists,
      entityNameColumnConfiguration,
      implementedSchemaInterfaces,
    });
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: RegistrationStructuredTableMetadata;
  }): StructuredTableColumn {
    if (
      tableMetadata.implementedSchemaInterfaces != null &&
      tableMetadata.implementedSchemaInterfaces.length > 0
    ) {
      const frontEndMetadata = getFrontEndMetadataForInterfaces(
        column,
        tableMetadata.implementedSchemaInterfaces
      );
      if (frontEndMetadata != null) {
        return {
          ...column,
          frontEndMetadata,
        };
      }
    }
    return column;
  }

  override getActionFunctions(
    tableMetadata: RegistrationStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<RegistrationStructuredTableMetadata, {}> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    const secondary = [
      // TODO: Figure out if this use of ! is safe.
      ...inherited.secondary!,
      createRequestV2SubmissionActionFunction,
      createProcessActionFunction,
      createRequestActionFunction,
      addToWorklistActionFunction,
      updateRegistrationTableSettingsActionFunction,
      registrationTableArchiveBioentitiesActionFunction,
      createDatasetWithPlotsActionFunction,
    ];
    if (shouldValidateStructuredTableSchema(StructuredTableKind.REGISTRATION, schemas)) {
      secondary.push(validateStructuredTableActionFunction);
    }

    return {
      primary: [registerActionFunction],
      secondary,
      // TODO: Figure out if this use of ! is safe.
      overflow: [...inherited.overflow!, printBarcodesActionFunction],
      contextMenu: [registerRowsActionFunction],
      footer: inherited.footer ?? [],
    };
  }

  override getDomainGraphTypes(schemas: Array<SchemaAndColumnGroup>): Array<StructuredTableDomainGraphType> {
    // TODO(BNCH-66819): Remove - introspection API should ideally let us determine
    // object type from hydrated schema without this sort of case work.
    const types: Array<StructuredTableDomainGraphType> = [];
    for (const schema of schemas) {
      if (isTagSchema(schema)) {
        const objectType = getObjectTypeForModelTypeConstant(schema.folderItemType);
        if (objectType != null) {
          types.push({
            objectSpec: {objectType},
            columnGroup: schema.columnGroup,
            columnType: TableColumnType.EXISTING_BIOENTITY,
          });
        }
      }
    }
    return types;
  }

  /**
   * Get the registration table's tag schema.
   * Returns null if the schema is inaccessible or nonexistent.
   */
  getTagSchema(structuredTable: RegistrationStructuredTable): RegistryConstants.TagSchema | null {
    return getTagSchemaForStructuredTable(structuredTable);
  }

  getSpreadsheetTypeForColumn(
    column: StructuredTableColumn
  ): SpreadsheetBulkImportConstants.COLUMN_TYPE | null {
    return getSpreadsheetTypeForColumn(ModelTypeConstants.TAG_SCHEMA, column);
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: RegistrationStructuredTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<
    StructuredTableAddOperation<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata>
  > {
    const ops: Array<
      StructuredTableAddOperation<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata>
    > = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
          });
        },
        dataTestAttrs: {
          component: 'RegistrationStructuredTable',
          key: 'addFromTable',
        },
      },
    ];
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.REGISTRATION_TABLE
          );
        },
      });
    }

    // `tagSchemaItemType` is persisted in the structured table metadata so that it can be used here. If we
    // support spreadsheet import for all entity types in the future (and remove this gate), then we can
    // consider removing this metadata key as it would be unused.
    const tagSchemaItemType = structuredTable
      ? inferTagSchemaItemTypeFromMetadata(structuredTable.metadata)
      : null;
    // Check if spreadsheet import is enabled for this entity type.
    // Default to true (show option) for unknown types to maintain backward compatibility.
    //
    // NOTE: We are passing `tagSchemaItemType` directly to the feature check instead of the full
    // `creatableEntityType` for simplicity. Can change later if needed.
    const shouldShowAddFromSpreadsheet = tagSchemaItemType
      ? isEntityCreationFeatureEnabled(
          tagSchemaItemType,
          EntityCreationFeatureType.ADD_FROM_SPREADSHEET_TO_REGISTRATION_TABLE
        )
      : true;
    if (shouldShowAddFromSpreadsheet) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
        onSelect: async ({structuredTable, addFullRows}) => {
          const tagSchema = this.getTagSchema(structuredTable);
          assert(
            tagSchema != null,
            'Tried to import spreadsheet into registration table with inaccessible or nonexistent schema'
          );

          const rows = await importRowsFromSpreadsheet({
            structuredTable,
            schemas: {tagSchema},
            getSpreadsheetTypeForColumn: (column) =>
              getSpreadsheetTypeForColumn(ModelTypeConstants.TAG_SCHEMA, column),
          });

          if (rows) {
            addFullRows(rows);
          }
        },
        dataTestAttrs: {
          component: 'RegistrationStructuredTable',
          key: 'addFromSpreadsheet',
        },
      });
    }

    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }

    return ops;
  }

  /**
   * Return all of the bioentities in our EXISTING_BIOENTITY column.
   */
  // Keep in sync with RegistrationTableInstantiator.get_primary_sample_ids_for_row (which delegates to
  // get_bioentity_primary_sample_ids) in
  // benchling/notebook/structured_tables/instantiators/registration_table_instantiator.py (tested by
  // TestRegistrationInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/registration_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: RegistrationStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    return getBioentityPrimarySamplesFromStructuredTable(structuredTable, computedTable);
  }

  override getTableKindRowUpdater(): typeof RegistrationTableRowUpdater {
    return RegistrationTableRowUpdater;
  }

  /**
   * Return a customEval function based on the column type. When our insert type is
   * REGISTER_EXISTING_BIOENTITIES, we show an error during evaluation if the provided entity is already
   * registered.
   *
   * If the row has been finalized, that means it was registered as part of our table and we don't want
   * to do any additional validation.
   *
   * @param column: the column in which our custom eval for a cell is taking place in
   * @param row: the row in which our custom eval for a cell is taking place in
   * @param metadata: table metadata that includes whether the table is for registering existing entities
   */

  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: RegistrationStructuredTableMetadata;
    rowMetadata: RegistrationStructuredTableRowMetadata;
  }> | null {
    const {column, row} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const metadata = tableView.metadata;
    if (row.isFinalized) {
      return null;
    }
    if (column.columnType === TableColumnType.BASES) {
      return {customEvalFn: basesColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
    }
    if (column.columnType === TableColumnType.ANTIBODY_DOMAIN) {
      return {customEvalFn: antibodyDomainColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
    }
    if (column.columnType !== TableColumnType.EXISTING_BIOENTITY) {
      if (metadata.insertType !== RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES) {
        switch (column.columnType) {
          case TableColumnType.SMILES:
            return {customEvalFn: smilesColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
          // TODO BNCH-52395: Unify oligo and BASES table column types.
          case TableColumnType.DNA_HELM:
          case TableColumnType.RNA_HELM:
            return {customEvalFn: basesHELMColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
          case TableColumnType.DNA_IDT:
          case TableColumnType.RNA_IDT:
            return {customEvalFn: basesIDTColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
          default:
            return {customEvalFn: lookupTagValue, customEvalType: CustomEvalType.DEFAULT};
        }
      } else {
        return {customEvalFn: lookupTagValue, customEvalType: CustomEvalType.DEFAULT};
      }
    }
    return {customEvalFn: checkRegisteredStatus, customEvalType: CustomEvalType.DEFAULT};
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata>,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns, metadata: tableMetadata} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    if (
      tableMetadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES &&
      columns[currentColumnIndex].columnType !== TableColumnType.EXISTING_BIOENTITY
    ) {
      const bioentityIdx = columns.findIndex(
        ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
      );
      return [`=${toSpreadsheetIndex({row: currentRowIndex, column: bioentityIdx})}`];
    }
    return [];
  }

  override getSampleInfosForRequest(
    structuredTable: RegistrationStructuredTable
  ): Array<SampleLookupInfoForCreateRequest> {
    const primarySamplesByRowId: {
      [rowId: string]: Array<PrimarySample>;
    } = getBioentityPrimarySamplesFromStructuredTable(structuredTable, undefined, true);
    const sampleLookupInfos: Array<SampleLookupInfo> = Object.values(primarySamplesByRowId).flatMap(
      (primarySamples) => primarySamples.flatMap((primarySample) => primarySample.sampleLookupInfo ?? [])
    );
    return sampleLookupInfos;
  }

  /**
   * Return a validation function that checks for duplicate bioentities in the
   * existing bioentity column, if we are in a REGISTER_EXISTING_BIOENTITIES
   * mode.
   */
  override getTableValidationFns(): Array<
    TableValidationFn<RegistrationStructuredTableMetadata, RegistrationStructuredTableRowMetadata>
  > {
    return [...super.getTableValidationFns(), getErrorsForDuplicateExistingBioentitiesInNonFinalizedRows];
  }

  override getStoreDataForLiveValues(
    structuredTable: StructuredTable<RegistrationTableMetadata, RegistrationStructuredTableRowMetadata>
  ): {
    fileSummariesById: {[bioentityId: string]: BioentitySummary};
  } {
    return {
      fileSummariesById: getStore().getState().models.fileSummariesById,
    };
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: StructuredTable<
      RegistrationStructuredTableMetadata,
      RegistrationStructuredTableRowMetadata
    >
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    // The primary column will always be the registered entity
    const primaryColumnIndex = structuredTable.columns.findIndex(
      (column) => column.columnType === TableColumnType.EXISTING_BIOENTITY
    );
    const schema = getTagSchemaForStructuredTable(structuredTable);
    const folderItemType = schema?.folderItemType ?? null;
    assert(primaryColumnIndex !== -1, 'Found registration table without an Existing Bioentity column');
    return [
      {
        validDestinationColumnRequirements: [
          {
            columnType: TableColumnType.TAG_VALUE,
            schemaFieldType: FieldType.BIOENTITY_LINK,
            folderItemType,
          },
          {
            columnType: structuredTable.columns[primaryColumnIndex].columnType,
            schemaFieldType: null,
            folderItemType,
          },
        ],
        schemaIdConstraint: schema?.id ?? null,
        columnIndex: primaryColumnIndex,
      },
    ];
  }

  override areRequiredSchemasArchived(
    structuredTable: StructuredTable<RegistrationTableMetadata, RegistrationStructuredTableRowMetadata>
  ): boolean {
    const tagSchema = getTagSchemaForStructuredTable(structuredTable);
    if (tagSchema && isArchived(tagSchema)) {
      return true;
    }
    return false;
  }

  override isReadOnly(): boolean {
    return !isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE});
  }

  override allowMergedCellRangeInColumn(
    structuredTable: RegistrationStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    return ![
      TableColumnType.EXISTING_BIOENTITY,
      TableColumnType.BIOENTITY_NAME,
      TableColumnType.ALIAS,
    ].includes(column.columnType);
  }

  override enableCustomColumns(structuredTable: RegistrationStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: RegistrationStructuredTable): boolean {
    return true;
  }
}
